// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngCarousel from "@awc/App/Components/XngCarousel";
import ProductCarouselProduct, { ProductCarouselProductStyles } from "@awc/Overrides/Components/ProductCarouselProduct";
import { fontConfig } from "@awc/XDL/PreStyle/typography";
import { xScreen } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductInfoFromList,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import React, { useContext, useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    titleAlignment = "titleAlignment",
    carouselType = "carouselType",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    seedWithManuallyAssigned = "seedWithManuallyAssigned",
    showImage = "showImage",
    showTitle = "showTitle",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    showAvailability = "showAvailability",
    showAttributes = "showAttributes",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titleAlignment]: string;
        [fields.carouselType]: string;
        [fields.numberOfProductsToDisplay]: number;
        [fields.seedWithManuallyAssigned]: string;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAttributes]: boolean;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const products = getProductsForProductInfoList(state, ownProps.id);
    const quoteProducts = (product: ProductModel) =>
        Number(
            product.quoteRequired ||
                getProductInfoFromList(state, ownProps.id, product.id)?.pricing?.unitNetPrice === 0,
        );

    return {
        products: products?.sort((a, b) => quoteProducts(a) - quoteProducts(b)),
        errorMessage: getErrorMessage(state, ownProps.id),
        session: state.context.session,
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    HasParentProductId;

export interface ProductDetailsCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    wrapper?: InjectableCss;
    carouselLayout?: InjectableCss;
    mobileCarousel?: InjectableCss;
    currentProductColumn?: InjectableCss;
    carouselColumn?: InjectableCss;
    card?: InjectableCss;
    cardHeader?: InjectableCss;
    currentCardHeader?: InjectableCss;
    cardHeaderText?: TypographyPresentationProps;
    carouselProductStyles?: ProductCarouselProductStyles;
}

export const productDetailsCarouselStyles: ProductDetailsCarouselStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            margin-bottom: 10px;
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    carouselLayout: {
        css: css`
            display: flex;
            gap: 18px;
            width: 100%;
            align-items: stretch;
            overflow: hidden;

            ${xScreen("lte", "sm")} {
                display: none;
            }
        `,
    },
    mobileCarousel: {
        css: css`
            display: none;

            ${xScreen("lte", "sm")} {
                display: block;

                .swiper-wrapper {
                    align-items: stretch;
                }

                .swiper-slide {
                    width: 243px;
                    height: auto;
                    display: flex;
                }

                .swiper-slide > div {
                    width: 100%;
                    height: 100%;
                }
            }
        `,
    },
    currentProductColumn: {
        css: css`
            width: 243px;
            flex: 0 0 243px;
            margin-bottom: 20px;
        `,
    },
    carouselColumn: {
        css: css`
            min-width: 0;
            flex: 1 1 auto;

            .swiper-slide {
                width: 243px;
                height: auto;
                display: flex;
            }

            .swiper-slide > div {
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
            }
        `,
    },
    card: {
        css: css`
            border: 1px solid ${getColor("common.border")};
            height: 100%;
            min-height: 255px;
            display: flex;
            flex-direction: column;
            flex: 1;
        `,
    },
    cardHeader: {
        css: css`
            padding: 8px 12px;
            background: ${getColor("text.main")};
        `,
    },
    currentCardHeader: {
        css: css`
            padding: 8px 12px;
            background: ${getColor("primary.main")};
        `,
    },
    cardHeaderText: {
        color: "common.background",
        weight: fontConfig.weight.bold,
        size: fontConfig.fontSize.regular,
        lineHeight: fontConfig.lineHeight.singleLineRegular,
    },
    carouselProductStyles: {
        productGridContainerStyle: {
            container: {
                css: css`
                    border: 0;
                    border-top: 0;
                    padding: 8px 12px 12px;
                    min-height: 225px;

                    & > .bottom-section {
                        gap: 10px;
                        margin-top: auto;
                    }
                `,
            },
            info: {
                css: css`
                    gap: 4px;
                `,
            },
            addToCartActions: {
                css: css`
                    row-gap: 4px;
                `,
            },
            addToList: {
                css: css`
                    justify-content: flex-end;
                    margin-top: 0;
                    min-height: 14px;
                    &:empty {
                        display: block;
                    }
                `,
            },
            image: {
                css: css`
                    width: 219px;
                    height: 219px;
                    &,
                    img {
                        aspect-ratio: 1;
                    }
                `,
            },
        },
        productDescriptionStyles: {
            productDetailLink: {
                typographyProps: {
                    size: fontConfig.fontSize.regular,
                    weight: fontConfig.weight.bold,
                    lineHeight: fontConfig.lineHeight.singleLineRegular,
                },
                css: css`
                    margin-bottom: 0;
                `,
            },
        },
        attributes: {
            container: {
                css: css`
                    > div:first-child span {
                        color: ${getColor("secondary.main")};
                    }
                `,
            },
            attributeTypeText: {
                size: fontConfig.fontSize.small,
                lineHeight: fontConfig.lineHeight.singleLineSmall,
            },
            attributeValueText: {
                size: fontConfig.fontSize.small,
                lineHeight: fontConfig.lineHeight.singleLineSmall,
            },
        },
        productPriceWrapper: {
            css: css`
                margin-bottom: 0;
            `,
        },
        productAddToCartButtonStyles: {
            css: css`
                width: 100%;
                min-height: 36px;
                height: 36px;
            `,
        },
        productAddToListLinkStyles: {
            link: {
                typographyProps: {
                    size: fontConfig.fontSize.xSmall,
                    lineHeight: fontConfig.lineHeight.singleLinexSmall,
                },
                css: css`
                    margin-bottom: 0;
                    min-height: 14px;
                `,
            },
        },
    },
};

const styles = productDetailsCarouselStyles;

const carouselTypeOptions = [
    { value: "crossSells", displayName: "Cross-Sell" },
    { value: "customersAlsoPurchased", displayName: "Also Purchased" },
    { value: "alternative", displayName: "Alternative" },
];

const productHeaderByType: Record<string, string> = {
    crossSells: "Cross-Sell Product",
    customersAlsoPurchased: "Also Purchased Product",
    alternative: "Alternative Product",
};

const defaultCustomCss = `.wrapper {
}

.titleText {
}`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const ProductDetailsCarousel = ({
    id,
    fields,
    products,
    errorMessage,
    loadCarouselProducts,
    shellContext,
    session,
    parentProductId,
}: Props) => {
    const productContext = useContext(ProductContext);
    const afterCarousel = useRef<HTMLSpanElement>(null);
    const currentProduct = productContext?.product;
    const currentProductInfo = productContext?.productInfo;

    useEffect(() => {
        loadCarouselProducts({
            isShellEdit: shellContext.isInShell && shellContext.isEditing,
            carouselId: id,
            carouselType: fields.carouselType,
            relatedProductType: "",
            seedWithManuallyAssigned: fields.seedWithManuallyAssigned,
            displayProductsFrom: "",
            selectedCategoryIds: [],
            numberOfProductsToDisplay: fields.numberOfProductsToDisplay,
            widgetPosition: 1,
            isProductDetailsPage: true,
            productId:
                fields.carouselType === "alternative" && parentProductId ? parentProductId : currentProduct?.id,
            isProductListPage: false,
            isBrandDetailsPage: false,
        });
    }, [
        fields.carouselType,
        fields.numberOfProductsToDisplay,
        fields.seedWithManuallyAssigned,
        currentProduct?.id,
        parentProductId,
        session?.pickUpWarehouse?.id,
    ]);

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    const carouselProducts = products?.filter(product => product.id !== currentProduct?.id);
    if (!currentProduct || !currentProductInfo || !carouselProducts?.length) {
        return null;
    }

    const title =
        fields.title ||
        translate(
            carouselTypeOptions.find(option => option.value === fields.carouselType)?.displayName ||
                "Product Details Carousel",
        );
    const carouselProductHeader = productHeaderByType[fields.carouselType] || "Product";
    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const renderProduct = (product: ProductModel, isCurrentProduct = false) => (
        <StyledWrapper {...styles.card}>
            <StyledWrapper {...(isCurrentProduct ? styles.currentCardHeader : styles.cardHeader)}>
                <Typography {...styles.cardHeaderText}>
                    {translate(isCurrentProduct ? "Current Product" : carouselProductHeader)}
                </Typography>
            </StyledWrapper>
            <ProductCarouselProduct
                carouselId={id}
                product={product}
                productInfo={isCurrentProduct ? currentProductInfo : undefined}
                onQtyOrderedChanged={isCurrentProduct ? productContext.onQtyOrderedChanged : undefined}
                onUnitOfMeasureChanged={isCurrentProduct ? productContext.onUnitOfMeasureChanged : undefined}
                showImage={fields.showImage}
                showBrand={false}
                showTitle={fields.showTitle}
                showPartNumbers={false}
                showPrice={fields.showPrice}
                showAddToCart={fields.showAddToCart}
                showAddToList={fields.showAddToList}
                showAttributes={fields.showAttributes}
                maximumNumberAttributeTypes={4}
                showAvailability={fields.showAvailability}
                showImageBadges={false}
                showTextBadges={false}
                showCustomBadge={false}
                extendedStyles={styles.carouselProductStyles}
            />
        </StyledWrapper>
    );

    return (
        <StyledWrapper {...customCssWrapper}>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <Typography
                    {...styles.titleText}
                    className="titleText"
                    css={css`
                        text-align: ${fields.titleAlignment};
                    `}
                >
                    {title}
                </Typography>
                <StyledWrapper
                    {...styles.carouselLayout}
                    data-test-selector={`productDetailsCarousel_${fields.carouselType}`}
                >
                    <StyledWrapper {...styles.currentProductColumn} data-test-selector="currentProduct">
                        {renderProduct(currentProduct, true)}
                    </StyledWrapper>
                    <StyledWrapper {...styles.carouselColumn}>
                        <XngCarousel spaceBetween={18} draggable={false} centerInsufficientSlides={false}>
                            {carouselProducts.map(product => (
                                <StyledWrapper key={product.id}>{renderProduct(product)}</StyledWrapper>
                            ))}
                        </XngCarousel>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper
                    {...styles.mobileCarousel}
                    data-test-selector={`productDetailsCarousel_${fields.carouselType}_mobile`}
                >
                    <XngCarousel spaceBetween={18} draggable={false} simulateTouch centerInsufficientSlides={false}>
                        {[currentProduct, ...carouselProducts].map((product, index) => (
                            <StyledWrapper key={product.id}>{renderProduct(product, index === 0)}</StyledWrapper>
                        ))}
                    </XngCarousel>
                </StyledWrapper>
            </StyledWrapper>
            <span ref={afterCarousel} tabIndex={-1} />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withParentProductId(connect(mapStateToProps, mapDispatchToProps)(withIsInShell(ProductDetailsCarousel))),
    definition: {
        group: "Product Details",
        icon: "gallery-thumbnails",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                sortOrder: 0,
                tab: basicTab,
            },
            {
                name: fields.titleAlignment,
                displayName: "Title Alignment",
                editorTemplate: "RadioButtonsField",
                defaultValue: "flex-start",
                horizontal: true,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
            },
            {
                name: fields.carouselType,
                displayName: "Carousel Type",
                editorTemplate: "DropDownField",
                options: carouselTypeOptions,
                defaultValue: "crossSells",
                fieldType: "Contextual",
                sortOrder: 2,
                tab: basicTab,
            },
            {
                name: fields.numberOfProductsToDisplay,
                displayName: "Number Of Products To Display",
                editorTemplate: "IntegerField",
                min: 1,
                max: 20,
                defaultValue: 10,
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "customersAlsoPurchased",
                sortOrder: 3,
                tab: basicTab,
            },
            {
                name: fields.seedWithManuallyAssigned,
                displayName: "Seed With Manually Assigned",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "customersAlsoPurchased",
                sortOrder: 4,
                tab: basicTab,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 5,
                tab: basicTab,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 6,
                tab: basicTab,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 7,
                tab: basicTab,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 8,
                tab: basicTab,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 9,
                tab: basicTab,
            },
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 10,
                tab: basicTab,
            },
            {
                name: fields.showAttributes,
                displayName: "Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 11,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (_item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
